import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, FlaskConical, Waves, PersonStanding, FileText, Check } from "lucide-react";
import Reveal from "@/components/Reveal";
import { fadeUp, viewport, stagger } from "@/lib/motion";
import { whatsappLink } from "@/lib/config";
import { WhatsAppGlyph } from "@/components/WhatsAppButton";
import { PACKAGES, inr } from "@/lib/packages";
import RiskChip from "@/components/RiskChip";
import SeatsLeft from "@/components/SeatsLeft";

const INCLUDES = [
  { icon: FlaskConical, title: "73+ laboratory biomarkers", desc: "Covering liver and metabolic health." },
  { icon: Waves, title: "Advanced Liver Imaging", desc: "Liver stiffness measurement and fat quantification." },
  { icon: PersonStanding, title: "120+ Health Data Points", desc: "Vitals, anthropometric measurements, detailed body composition analysis, grip strength, CGM, HRV, sleep analysis and more." },
  { icon: FileText, title: "Personalized Precision Analytics & Report", desc: "Risk insights and expert recommendations." },
];

export const FinalCTA = () => {
  const navigate = useNavigate();
  return (
    <section id="booking" className="relative overflow-hidden bg-sa-forest py-12 md:py-16" data-testid="booking-section">
      <div className="grain absolute inset-0" />
      <div className="relative mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <p className="mb-4 text-[13px] font-semibold uppercase tracking-[0.2em] text-sa-copper">
              Begin your journey
            </p>
            <h2 className="font-display text-4xl font-medium leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-[3.4rem]">
              Book your Precision Liver Evaluation.
            </h2>
            <p className="mt-6 max-w-md text-lg leading-relaxed text-white/60">
              Take the first physician-led step toward a healthier liver and a
              longer, stronger life. A single, focused visit that quantifies
              exactly what's happening inside your liver.
            </p>
            <div className="mt-8 flex items-center gap-3 text-sm text-white/50">
              <CheckCircle2 className="h-5 w-5 text-sa-copper" />
              No obligation — a member of our care team will call to confirm.
            </div>
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="booking-whatsapp"
              className="mt-6 inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/[0.05] px-5 py-3 text-sm font-medium text-white transition-[transform,background-color] duration-300 hover:scale-[1.03] hover:bg-white/10"
            >
              <WhatsAppGlyph className="h-4 w-4 text-[#25D366]" />
              Prefer to chat? Book on WhatsApp
            </a>
          </Reveal>

          <Reveal variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } } }}>
            <motion.div
              whileHover={{ y: -4 }}
              className="flex h-full flex-col justify-center rounded-[32px] border border-white/10 bg-white/[0.05] p-9 md:p-11"
              data-testid="booking-cta-card"
            >
              <div className="space-y-3.5" data-testid="landing-packages">
                {[PACKAGES.basic, PACKAGES.advanced].map((p) => (
                  <div key={p.key} className="flex flex-col gap-3.5 rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4" data-testid={`landing-package-${p.key}`}>
                    <div>
                      <div className="flex flex-wrap items-center gap-2.5">
                        <p className="font-display text-lg font-medium text-white">{p.name}</p>
                        <RiskChip risk={p.risk} hex={p.riskHex} text={p.riskText} />
                      </div>
                      <p className="mt-1 text-[12px] text-white/45">{p.biomarkers} biomarkers · full evaluation</p>
                    </div>
                    <div className="flex items-center gap-3 sm:flex-col sm:items-end sm:gap-0.5">
                      <span className="font-display text-2xl font-medium text-white">{inr(p.price)}</span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-[13px] uppercase tracking-[0.14em] text-white/40">
                Founder's offer · first 1,000 evaluations only
              </p>
              <SeatsLeft dark className="mt-3" />
              <p className="mt-4 text-[15px] leading-relaxed text-white/60">
                Home blood collection included at your address — pick the depth
                that matches your risk category.
              </p>
              <button
                onClick={() => navigate("/book")}
                data-testid="booking-open-modal"
                className="group mt-8 flex w-full items-center justify-center gap-2.5 rounded-full bg-sa-copper px-7 py-4 text-[15px] font-medium text-white transition-[transform,background-color] duration-300 hover:scale-[1.02] hover:bg-sa-copper-deep active:scale-[0.99]"
              >
                Book Evaluation
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <p className="mt-4 text-center text-[13px] text-white/40">
                Details → payment → appointment. Under a minute.
              </p>
            </motion.div>
          </Reveal>
        </div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:mt-12 lg:grid-cols-4"
        >
          {INCLUDES.map((inc) => {
            const Icon = inc.icon;
            return (
              <motion.div
                key={inc.title}
                variants={fadeUp}
                className="rounded-[28px] border border-white/10 bg-white/[0.04] p-7 transition-colors duration-300 hover:border-sa-copper/40"
              >
                <div className="flex items-center justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/5 text-sa-copper">
                    <Icon className="h-5 w-5" />
                  </span>
                  <Check className="h-5 w-5 text-sa-copper" />
                </div>
                <p className="mt-5 font-display text-lg font-medium text-white">{inc.title}</p>
                <p className="mt-1.5 text-sm leading-relaxed text-white/60">{inc.desc}</p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default FinalCTA;
